import React, { useEffect, useState } from "react";
import {
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import { DashboardViewModel } from "../../viewmodels/dashboard/DashboardViewModel";
import EmptyModal from "../../components/modals/EmptyModal";

interface DashboardProps {
  logout: () => void;
}

interface MetricCard {
  title: string;
  value: string | number;
  change: string;
  changeType: "positive" | "negative" | "neutral";
}

interface ActivityItem {
  type: string;
  message: string;
  time: string;
  time_ago: string;
}

interface DashboardApiResponse {
  metrics: {
    total_sales: number;
    orders_today: number;
    low_stock_count: number;
    active_orders: number;
  };
  chart_data: {
    labels: string[];
    data: number[];
  };
  top_items: Array<{
    name: string;
    sales: number;
  }>;
  top_customers: Array<{
    name: string;
    orders: number;
  }>;
  recent_activity: ActivityItem[];
}

const METRIC_ICONS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  "Total Sales": CurrencyDollarIcon,
  "Orders Today": ShoppingCartIcon,
  "Low Stock": ExclamationTriangleIcon,
  "Active Orders": ClipboardDocumentListIcon,
};

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="text-center py-8">
      <h4 className="text-sm font-medium apollo-heading mb-1" style={{ color: "var(--heading-primary)" }}>
        {title}
      </h4>
      <p className="text-xs apollo-body" style={{ color: "var(--nav-text-secondary)" }}>
        {description}
      </p>
    </div>
  );
}

const Dashboard: React.FC<DashboardProps> = ({ logout }) => {
  const {
    getStatsSummaries,
    getProductInventoryStats,
    getTopSellingItemsStats,
    fetchTopFiveSalesPersons,
    getChartData,
    statsSummaries,
    topSellingItemsStats,
    chartData,
    emptyModal,
    setEmptyModelOpen,
  } = DashboardViewModel(logout);

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          // getStatsSummaries(),
          // getProductInventoryStats(),
          // getTopSellingItemsStats(),
          // fetchTopFiveSalesPersons(),
          // getChartData(),
        ]);
        try {
          const response = await fetch("/api/dashboard/summary", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setDashboardData(data);
          }
        } catch (apiError) {
          console.log("Dashboard summary API not available, using existing data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const userName = localStorage.getItem("first_name");

  const metrics: MetricCard[] = dashboardData
    ? [
        {
          title: "Total Sales",
          value: `$${dashboardData.metrics.total_sales.toLocaleString()}`,
          change: "+12% vs LM",
          changeType: "positive",
        },
        {
          title: "Orders Today",
          value: dashboardData.metrics.orders_today.toString(),
          change: "+3 vs LY",
          changeType: "positive",
        },
        {
          title: "Low Stock",
          value: dashboardData.metrics.low_stock_count.toString(),
          change: "Alert",
          changeType: "negative",
        },
        {
          title: "Active Orders",
          value: dashboardData.metrics.active_orders.toString(),
          change: "Pending",
          changeType: "neutral",
        },
      ]
    : statsSummaries.length > 0
    ? [
        {
          title: "Total Sales",
          value: `$${(statsSummaries[0] as any)?.total_sales ?? 0}`,
          change: "From API",
          changeType: "positive" as const,
        },
        {
          title: "Orders Today",
          value: (statsSummaries[0] as any)?.orders_today ?? 0,
          change: "From API",
          changeType: "positive" as const,
        },
        {
          title: "Low Stock",
          value: (statsSummaries[0] as any)?.low_stock_count ?? 0,
          change: "From API",
          changeType: "neutral" as const,
        },
        {
          title: "Active Orders",
          value: (statsSummaries[0] as any)?.active_orders ?? 0,
          change: "From API",
          changeType: "neutral" as const,
        },
      ]
    : [
        { title: "Total Sales", value: "$0", change: "No data", changeType: "neutral" as const },
        { title: "Orders Today", value: "0", change: "No data", changeType: "neutral" as const },
        { title: "Low Stock", value: "0", change: "No alerts", changeType: "positive" as const },
        { title: "Active Orders", value: "0", change: "No pending", changeType: "neutral" as const },
      ];

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div
          className="content-card px-4 sm:px-6 lg:px-8 py-6 mb-8"
          style={{ borderBottom: "var(--nav-border)" }}
        >
          <h1 className="text-2xl font-bold apollo-heading" style={{ color: "var(--heading-primary)" }}>
            Welcome back, {userName}
          </h1>
          <p className="text-sm mt-1 apollo-subheading" style={{ color: "var(--heading-secondary)" }}>
            Loading your business insights...
          </p>
        </div>
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="content-card p-6">
                <div className="animate-pulse">
                  <div className="h-4 rounded w-24 mb-2" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="h-8 rounded w-16 mb-2" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="h-3 rounded w-20" style={{ backgroundColor: "#e2e8f0" }} />
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            <div className="lg:col-span-2">
              <div className="content-card p-6">
                <div className="animate-pulse">
                  <div className="h-6 rounded w-32 mb-6" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="h-64 rounded" style={{ backgroundColor: "#e2e8f0" }} />
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="content-card p-6">
                <div className="animate-pulse">
                  <div className="h-6 rounded w-24 mb-4" style={{ backgroundColor: "#e2e8f0" }} />
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full" style={{ backgroundColor: "#e2e8f0" }} />
                        <div className="h-4 rounded flex-1" style={{ backgroundColor: "#e2e8f0" }} />
                        <div className="h-4 rounded w-12" style={{ backgroundColor: "#e2e8f0" }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
        

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
};

export default Dashboard;
